import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Loader2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/api/client';

const PROVIDERS = [
  {
    id: 'shippo',
    title: 'Shippo',
    blurb: 'Parcels from USPS, UPS, FedEx and DHL, including Media Mail.',
  },
  {
    id: 'easypost',
    title: 'EasyPost',
    blurb:
      'Adds First-Class letters and flats, and Library Mail, from USPS. Letters and flats cannot be refunded.',
  },
];

const MODE_LABELS = { test: 'Test key', live: 'Live key', unknown: 'Key not recognised' };

/**
 * Which shipping service buys new labels on the Mail page.
 *
 * A label always refunds through the service that bought it, so changing this
 * never strands one bought earlier. Bulk uploads, manifests and address checks
 * stay on Shippo whatever is chosen here.
 */
export default function ShippingProviderSection() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['shippingProvider'],
    queryFn: () => api.request('/settings/shipping-provider'),
  });

  const switchMutation = useMutation({
    mutationFn: (provider) =>
      api.request('/settings/shipping-provider', { method: 'PUT', body: { provider } }),
    onSuccess: ({ provider }) => {
      queryClient.invalidateQueries({ queryKey: ['shippingProvider'] });
      // The test-mode banner follows the active provider's key.
      queryClient.invalidateQueries({ queryKey: ['version'] });
      toast.success(`New labels will be bought from ${provider === 'easypost' ? 'EasyPost' : 'Shippo'}`);
    },
    onError: (error) => toast.error(error.message ?? 'Could not change the shipping service'),
  });

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-slate-100 to-slate-50">
        <CardTitle className="flex items-center gap-2">
          <Truck className="w-5 h-5" />
          Shipping service
        </CardTitle>
      </CardHeader>

      <CardContent className="p-6 space-y-4">
        <Alert className="border-slate-200 bg-slate-50">
          <AlertCircle className="h-4 w-4 text-slate-600" />
          <AlertDescription className="text-slate-700">
            Chooses where new Mail-page labels are bought. Labels already bought are refunded
            through the service that bought them. Add the service&apos;s API key above before
            choosing it.
          </AlertDescription>
        </Alert>

        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {PROVIDERS.map((provider) => {
              const status = data?.[provider.id];
              const active = data?.provider === provider.id;

              return (
                <div
                  key={provider.id}
                  className={`rounded-lg border p-4 space-y-3 ${
                    active ? 'border-green-300 bg-green-50' : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-slate-900">{provider.title}</p>
                    {active && (
                      <Badge className="bg-green-600 gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        In use
                      </Badge>
                    )}
                  </div>

                  <p className="text-sm text-slate-600">{provider.blurb}</p>

                  <p className="text-xs text-slate-500">
                    {status?.configured
                      ? `Key set · ${MODE_LABELS[status.mode] ?? status.mode}`
                      : 'No API key yet'}
                  </p>

                  {!active && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!status?.configured || switchMutation.isPending}
                      onClick={() => switchMutation.mutate(provider.id)}
                    >
                      {switchMutation.isPending && switchMutation.variables === provider.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        `Use ${provider.title}`
                      )}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
